// PR6 — the DS mobile layout's chrome (artboards 3a-3e, ui_kits/mobile/Mobile.jsx's `TabBar`):
// an 86px bottom tab bar with a raised ink Record button, replacing AppShell's sidebar and
// top bar. AppShell switches to this component when useIsMobileLayout() is true; see that
// file's render for the one switch point.
//
// Deliberately thin: Home, Library and Meetings keep rendering their own existing content
// (greeting, filter chips, event cards, ...) inside `.mshell__content` — only the surrounding
// chrome changes. `env(safe-area-inset-*)` keeps the bar clear of the home indicator / nav bar
// on both the Android and iOS Tauri shells (SD32), and `100dvh` keeps the layout correct when
// the on-screen keyboard resizes the visual viewport.
import type { ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";
import { Icon } from "@grackle/ui";
import type { OrgOut } from "../api/types";
import type { AppSection } from "./AppShell";
import { homePath, libraryPath } from "../library/routes";
import "./MobileShell.css";

const VERIFY_TO_RECORD = "Verify your email to record";

export function MobileShell({
  org,
  section,
  canRecord = true,
  children,
}: {
  org: OrgOut;
  section: AppSection;
  canRecord?: boolean;
  children: ReactNode;
}) {
  const location = useLocation();
  const base = homePath(org.slug);
  const libraryRoute = libraryPath(org.slug);
  const meetingsRoute = `${base}/meetings`;
  // "shared" is the library under a server-side filter (FR-L4a) — still the Library tab.
  const active = section === "shared" ? "library" : section;

  function tabClass(id: string): string {
    return `mshell__tab${active === id ? " is-active" : ""}`;
  }

  return (
    <div className="mshell">
      <div className="mshell__content">{children}</div>
      <nav className="mshell__tabbar" aria-label="Primary">
        <Link to={base} className={tabClass("home")} aria-current={active === "home" ? "page" : undefined}>
          <Icon name="house" size={24} fill={active === "home"} />
          <span>Home</span>
        </Link>
        <Link
          to={libraryRoute}
          className={tabClass("library")}
          aria-current={active === "library" ? "page" : undefined}
        >
          <Icon name="book-open" size={24} fill={active === "library"} />
          <span>Library</span>
        </Link>
        <Link
          to="/record"
          className="mshell__record"
          aria-label="New recording"
          aria-disabled={!canRecord}
          title={canRecord ? undefined : VERIFY_TO_RECORD}
          onClick={(e) => {
            if (!canRecord) e.preventDefault();
          }}
        >
          <span className="mshell__record-dot" />
        </Link>
        <Link
          to={meetingsRoute}
          className={tabClass("meetings")}
          aria-current={active === "meetings" ? "page" : undefined}
        >
          <Icon name="users-three" size={24} fill={active === "meetings"} />
          <span>Meetings</span>
        </Link>
        <Link
          to="/account"
          className={`mshell__tab${location.pathname === "/account" ? " is-active" : ""}`}
          aria-current={location.pathname === "/account" ? "page" : undefined}
        >
          <Icon name="gear-six" size={24} fill={location.pathname === "/account"} />
          <span>Settings</span>
        </Link>
      </nav>
    </div>
  );
}
